/* eslint-disable ember/no-runloop */
import { get } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { cancel, debounce } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { isMyHotelsId } from 'frontend-utils/utils/my-hotel-utils';
import { Promise as EmberPromise } from 'rsvp';

interface GeneralReportScheduleRecipientsMultipleSelectSignature {
  Args: {
    canCreateNewUser: unknown;
    canCreateOtherReports: unknown;
    currentHotel: unknown;
    currentMarket: unknown;
    edit: unknown;
    onChangeSelectedRecipients: unknown;
    selectedRecipients: unknown;
    user: unknown;
  };
}

export default class GeneralReportScheduleRecipientsMultipleSelect extends Component<GeneralReportScheduleRecipientsMultipleSelectSignature> {
  @service declare ajax: Services['ajax'];
  @service declare intl: Services['intl'];

  @tracked recipients = null;

  debounceId = null;
  isFetchingRecipients = false;

  @computed('ajax.host')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get host() {
    return this.ajax?.host;
  }

  @computed('args.{currentMarket.id,edit,canCreateOtherReports}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    // canCreateOtherReports is set by Backend for Staff users only
    return (
      !this.args.canCreateOtherReports ||
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      (isMyHotelsId(this.args.currentMarket?.id) && this.args.edit)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noResultsMessage() {
    let message = this.intl.t('general.no_results');
    if (this.args.canCreateNewUser) {
      message += `. ${this.intl.t('reports.no_results_setup_new_user')}`;
    }

    return message;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    if (!this.recipients) {
      return null;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.args.selectedRecipients.map(
      // @ts-expect-error: Incorrect type
      (selectedRecipient) =>
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        this.recipients.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (recipient) => recipient.id === selectedRecipient.id,
        ) || {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          id: selectedRecipient.id,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          label: selectedRecipient.email,
        },
    );
  }

  willDestroy(): void {
    super.willDestroy();

    // @ts-expect-error: Incorrect type
    cancel(this.debounceId);
  }

  // @ts-expect-error: Incorrect type
  fetchRecipients(term, resolve, reject): void {
    // For now, page one is used every time
    const data = {
      page: 0,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      q: term,
    };

    if (this.args.currentMarket) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data.market_id = this.args.currentMarket?.id;
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data.hotel_id = this.args.currentHotel?.id;
    }

    this.ajax
      .request('/reports/autocomplete/users/', {
        method: 'GET',
        data,
      })
      .then((result) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        const userIds = this.args.selectedRecipients.map((user) => user.id);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const results = result.results
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          .map((user) => ({ id: `${user.id}`, label: user.email }))
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          .filter((user) => userIds.indexOf(`${user.id}`) === -1);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        resolve(results);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      }, reject);
  }

  @action
  didReceiveRecipientsAttrs(): void {
    if (this.isDisabled && !this.args.edit) {
      // @ts-expect-error: Incorrect type
      this.args.onChangeSelectedRecipients([
        {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          id: this.args.user?.id,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          email: this.args.user?.email,
        },
      ]);
    } else {
      // Fetch all recipients first
      // (promise is created since this is neccesary for the search built in power select)
      new EmberPromise((resolve, reject) => {
        this.fetchRecipients('', resolve, reject);
      }).then((results) => {
        // @ts-expect-error: Incorrect type
        this.recipients = results;
      });
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  searchRecipients(term) {
    return new EmberPromise((resolve, reject) => {
      const debounceId = debounce(
        this,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.fetchRecipients,
        term,
        resolve,
        reject,
        300,
      );
      set(this, 'debounceId', debounceId);
    });
  }

  @action
  // @ts-expect-error: Incorrect type
  selectUser(users): void {
    // @ts-expect-error: Incorrect type
    this.args.onChangeSelectedRecipients(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      users.map((user) => ({ id: user.id, email: user.label })),
    );
  }

  <template>
    <div {{didInsert this.didReceiveRecipientsAttrs}}>
      {{#if this.isDisabled}}
        <div class="my-hotels-disabled-email">
          {{! @glint-expect-error: Incorrect type }}
          {{get @selectedRecipients "0.email"}}
        </div>
        {{#if @canCreateOtherReports}}
          <div
            class="my-hotels-disabled-message flex items-center justify-end gap-100 truncate"
          >
            <PrismIcon @name="circle-information-outline" @size="small" />
            {{t "reports.my_hotels_schedule"}}
          </div>
        {{/if}}
      {{else}}
        <div class="flex flex-col">
          <PrismComboSelectInput
            @isSearchable={{true}}
            @noMatchesMessage={{this.noResultsMessage}}
            @onChange={{this.selectUser}}
            @onSearch={{this.searchRecipients}}
            {{! @glint-expect-error: Incorrect type }}
            @options={{this.recipients}}
            @placeholder={{t "reports.add_recipient"}}
            @unsafe_isMultipleImmediate={{true}}
            @value={{this.value}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
